import type { ReactNode } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import { isRecentlyListed } from 'components/marketplace/helpers/certaintyPanel';
import { CardLink } from 'components/marketplace/PropertyCard';
import { FavoriteHeartIcon } from 'components/marketplace/shared/FavoriteHeartIcon';
import { formatCurrencyDollars } from 'helpers/formatters';

import { buildAddressLines } from '../helpers';
import type { ListingCardData } from '../types';
import { PdpStatusBadge } from './PdpStatusBadge';

import styles from './PdpSimilarListingCard.module.css';

export const PdpSimilarListingCard = ({
  listing,
  href,
  media,
  isLiked,
  isFavoriteLoading,
  onToggleFavorite,
}: {
  listing: ListingCardData;
  href: string;
  media: ReactNode;
  isLiked: boolean;
  isFavoriteLoading: boolean;
  onToggleFavorite: () => void;
}) => {
  const { primaryLine, secondaryLine } = buildAddressLines(listing);
  const locality = secondaryLine?.replace(/\s+\d{5}(?:-\d{4})?$/, '').trim();
  const title = [listing.street || primaryLine, locality].filter(Boolean).join(', ') || 'View home';
  const facts = [
    listing.beds == null ? null : `${listing.beds} bd`,
    listing.baths == null ? null : `${listing.baths} ba`,
    listing.livingSquareFootage == null
      ? null
      : `${listing.livingSquareFootage.toLocaleString('en-US', { notation: 'compact', maximumFractionDigits: 1 })} sqft`,
  ].filter(Boolean);
  let badge: ReactNode = null;
  if (listing.listingState === 'IN_CONTRACT')
    badge = <PdpStatusBadge label="Pending sale" tone="neutral" />;
  else if (listing.listingState === 'AVAILABLE_SOON')
    badge = <PdpStatusBadge label="Coming soon" tone="neutral" />;
  else if (listing.listingState === 'RECENTLY_SOLD')
    badge = <PdpStatusBadge label="Sold" tone="neutral" />;
  else if (listing.displayBadge === 'TEMPORARILY_UNAVAILABLE')
    badge = <PdpStatusBadge label="Temporarily unavailable" tone="neutral" />;
  else if (
    listing.listingState === 'ON_THE_MARKET' &&
    isRecentlyListed(listing.daysOnMarket ?? null)
  )
    badge = <PdpStatusBadge label="Newly listed" tone="new" />;
  return (
    <Col
      className={`group/similar-card relative isolate min-w-0 gap-4 rounded-[14px] ${styles.card}`}
    >
      <Box className="relative z-2 aspect-[303/226] w-full overflow-hidden rounded-[14px] bg-od-bg-tertiary">
        {media}
        {badge && <Box className="pointer-events-none absolute top-3 left-3 z-1">{badge}</Box>}
        <Row className="absolute top-3 right-3 z-2 gap-2">
          <Button
            variant="secondary"
            size="sm"
            analyticsName="pdp-similar-favorite"
            aria-label={isLiked ? 'unfavorite' : 'favorite'}
            loading={isFavoriteLoading}
            onClick={onToggleFavorite}
            className={`group/favorite size-7 rounded-full p-0 ${styles.glassButton}`}
          >
            <FavoriteHeartIcon filled={isLiked} interactive size={16} />
          </Button>
        </Row>
      </Box>
      <CardLink
        href={href}
        ariaLabel={`Property details: ${title}`}
        analyticsName="pdp-similar-title"
        className={`min-w-0 gap-1 px-2 pb-3 text-left ${styles.cardLink}`}
      >
        <Text as="p" className="text-label-md text-od-content-secondary">
          {listing.listPrice == null
            ? 'Price coming soon'
            : formatCurrencyDollars(listing.listPrice)}
        </Text>
        <Text as="span" className="text-label-sm text-od-content-primary">
          {title}
        </Text>
        <Text as="p" className="min-h-5 whitespace-pre-wrap text-body-sm text-od-content-secondary">
          {facts.join(' · ')}
        </Text>
      </CardLink>
    </Col>
  );
};
